<script setup lang="ts">
import { getAssetsUrlForCDN } from '@/utils/template';

// Casco dos modais de tela cheia empilhados por cima do hub Club Fidelidade: o
// painel da roletinha no estado disponível (fundo #000717, topbar com a logo
// centrada e o X à direita, partículas azuis subindo no fundo), colado no rodapé
// no mobile e flutuante no desktop.
//
// Quem usa: PostSheet (promoção do blog) e RewardsSheet (prêmios). O
// CashbackSheet NÃO usa — ele é o card centrado, outro formato.
//
// Só a área do slot rola; o painel clipa. `visible` mantém o overlay montado até
// o slide-down terminar e `sheetOpen` dispara a animação — mesma mecânica do
// hub e da roletinha.
type Props = {
  open: boolean;
  // Título opcional na área de conteúdo (o post não usa: ele tem o próprio H1
  // dentro do HTML do CMS).
  title?: string;
  // Largura do painel no desktop. No mobile é sempre 480px de conteúdo, igual
  // ao hub.
  desktopMaxWidth?: string;
  // Fundo do painel INTEIRO — topbar incluída. Existe porque conteúdo que pinta
  // o próprio fundo (a tela de níveis, com o glow do elo ancorado no topo) só
  // alcança a área de rolagem: o brilho morria na linha da topbar e a faixa da
  // logo ficava chapada. Passando o fundo por aqui ele nasce ATRÁS de tudo.
  panelBackground?: string;
  // Padding lateral da área de rolagem NO MOBILE. Existe pro post, cujo HTML do
  // CMS traz blocos que sangram até a borda; no desktop o padding é sempre 16px.
  // A topbar não é afetada — o vão dela é próprio, senão o X colaria na borda.
  mobileContentPaddingX?: string;
  // Volume do campo de partículas (0 a 1; 0 desliga). O padrão aqui é METADE do
  // que a roletinha usa: lá o campo tem o painel quase todo pra si, aqui ele
  // fica atrás de conteúdo denso que ainda por cima chega sem fundo próprio (o
  // rewards zera o card do `UserBox`), e no volume cheio as partículas
  // atravessavam a lista inteira.
  // A tela de níveis passa 0: ela traz as PRÓPRIAS fagulhas, e as duas camadas
  // somadas eram o pior caso.
  particlesIntensity?: number;
};

const {
  open,
  title = '',
  desktopMaxWidth = '860px',
  panelBackground = '#000717',
  mobileContentPaddingX = '16px',
  particlesIntensity = 0.5,
} = defineProps<Props>();

type Emits = {
  close: [];
  // Fim do slide-up: é onde o consumidor carrega conteúdo pesado, pra não
  // competir com a animação.
  opened: [];
  // Fim do slide-down: onde o consumidor solta o conteúdo.
  closed: [];
};

const emit = defineEmits<Emits>();

// Wordmark branco da Apostatudo — o mesmo arquivo da topbar da roletinha.
const logo = getAssetsUrlForCDN('/assets/images/roleta/logo.png');

// Partículas azuis, a MESMA cor da roletinha (o check-in é o dourado).
const ACCENT_COLOR = '#5AA9FF';

const visible = ref(false);
const sheetOpen = ref(false);

// Trava o scroll do fundo enquanto o modal está visível. O hub atrás já tem o
// próprio lock; o composable conta referências, então os dois convivem.
useBodyScrollLock(visible);

watch(
  () => open,
  async (isOpen) => {
    if (isOpen) {
      visible.value = true;
      await nextTick();
      sheetOpen.value = true;
    } else {
      sheetOpen.value = false;
    }
  },
  { immediate: true },
);

function onClosed() {
  visible.value = false;
  emit('closed');
}

function close() {
  emit('close');
}

// Quem rola aqui é o painel, não a window: conteúdo embutido que troca de tela
// (listagem ↔ post/promoção) chamava `window.scrollTo` e não subia nada — a tela
// nova abria no meio, no ponto onde a lista estava. O consumidor pega o sheet
// por `ref` e chama isto na troca.
const scrollRef = ref<HTMLElement | null>(null);

function scrollToTop() {
  scrollRef.value?.scrollTo({ top: 0 });
}

defineExpose({ scrollToTop });
</script>

<template>
  <Teleport to="body">
    <!-- dragstart.prevent por delegação: nada dentro do modal pode ser
         arrastado (o `user-drag` do CSS não cobre Firefox). -->
    <div
      v-if="visible"
      class="lhs-overlay"
      @dragstart.prevent
    >
      <div
        class="lhs-backdrop"
        :class="{ 'lhs-backdrop--out': !sheetOpen }"
        @click="close"
      />

      <Transition
        name="lhs-slide-up"
        @after-enter="emit('opened')"
        @after-leave="onClosed"
      >
        <div
          v-if="sheetOpen"
          class="lhs-screen"
          :style="{
            '--lhs-desktop-width': desktopMaxWidth,
            '--lhs-content-px': mobileContentPaddingX,
            background: panelBackground,
          }"
        >
          <!-- Partículas no rés do chão do painel, atrás de tudo. -->
          <BaseParticlesField
            class="lhs-particles"
            :color="ACCENT_COLOR"
            :intensity="particlesIntensity"
            travel="560px"
          />

          <!-- Topbar da roletinha: logo centrada, X à direita. O vão da
               esquerda é um espaçador da largura do X — é o que mantém a logo
               no centro ÓTICO sem posicionamento absoluto. -->
          <header class="lhs-topbar">
            <span
              aria-hidden="true"
              class="lhs-icon"
            />

            <img
              alt="Apostatudo"
              class="lhs-logo"
              :src="logo"
            />

            <button
              aria-label="Fechar"
              class="lhs-icon lhs-close"
              type="button"
              @click="close"
            >
              <svg
                aria-hidden="true"
                fill="none"
                viewBox="0 0 30 30"
              >
                <rect
                  fill="#fff"
                  fill-opacity="0.05"
                  height="30"
                  rx="15"
                  width="30"
                />
                <path
                  d="M19.2784 12.1583L20 11.4399L18.5601 10L17.8417 10.7184L15 13.5601L12.1583 10.7184L11.4399 10L10 11.4399L10.7184 12.1583L13.5601 15L10.7216 17.8417L10 18.5601L11.4399 20L12.1583 19.2816L15 16.4399L17.8417 19.2816L18.5601 20L20 18.5601L19.2816 17.8417L16.4399 15L19.2816 12.1583H19.2784Z"
                  fill="#fff"
                />
              </svg>
            </button>
          </header>

          <!-- ARMADILHA: NÃO colar `.blog-content-sandbox` (ou a irmã de
               promotions) neste elemento. Aquelas classes levam
               `overflow: visible !important` (assets/css/main.scss) pra impedir
               que o CSS do post esconda o conteúdo — no elemento que rola, elas
               matariam o scroll container. -->
          <div
            ref="scrollRef"
            class="lhs-scroll"
          >
            <p
              v-if="title"
              class="lhs-title"
            >
              {{ title }}
            </p>

            <slot />
          </div>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<!-- Slide-up/fade sem scope: @keyframes contornam o reset global de transition
     (mesma estratégia do hub e da roletinha). O painel é colado no rodapé e
     ocupa a tela toda, então a subida é a de 100% do hub. -->
<style>
.lhs-slide-up-enter-active {
  animation: lhs-slide-up 0.32s cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
  will-change: transform, opacity;
}
.lhs-slide-up-leave-active {
  animation: lhs-slide-up 0.24s cubic-bezier(0.4, 0, 1, 1) reverse forwards;
  will-change: transform, opacity;
}
@keyframes lhs-slide-up {
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>

<style scoped lang="scss">
// z-9994: acima do hub Club Fidelidade (9990) e abaixo do cashback (9995), do
// check-in (9996), do promocode (9997) e da roletinha (9999).
.lhs-overlay {
  @apply fixed inset-0 z-[9994] flex flex-col justify-end select-none;

  img {
    -webkit-user-drag: none;
  }
}

.lhs-backdrop {
  @apply absolute inset-0;

  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(4px);
  animation: lhs-fade-in 0.25s ease forwards;
}

.lhs-backdrop--out {
  animation: lhs-fade-in 0.24s ease reverse forwards;
}

@keyframes lhs-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

// Mesma caixa do hub (480px de conteúdo), com o fundo do painel da roletinha.
// Sobra de 100px no topo (mais a safe area) pra ler como modal SOBREPOSTO ao hub,
// e não como uma tela nova: o painel é colado no rodapé e os cantos de cima são
// arredondados. `clip` e não `hidden`: `hidden` é scroll container, e aí o
// ajuste de foco do clique desloca o painel (o bug das bordas da roletinha).
.lhs-screen {
  @apply relative z-10 flex flex-col w-full;

  max-width: 480px;
  height: calc(100dvh - 100px - env(safe-area-inset-top, 0px));
  margin: 0 auto;
  // Só o vertical: o padding lateral vive na topbar e na área de rolagem, pra que
  // o conteúdo possa ir até a borda sem desalinhar a logo/X.
  padding: 14px 0 0;
  // O fundo vem inline (`panelBackground`), com o #000717 de sempre como padrão.
  border-radius: 22px 22px 0 0;
  overflow: clip;
  isolation: isolate;
}

// Partículas no chão do painel e o conteúdo um degrau acima: sem
// `position: relative` o conteúdo não participa da ordem de pilha e a camada em
// z-0 pinta por cima dele, por mais que ela venha antes no DOM.
.lhs-particles {
  z-index: 0;
}

.lhs-topbar {
  @apply relative z-[1] flex items-center justify-between w-full shrink-0;

  padding-inline: 16px;
}

// Tamanho do X da roletinha (30px). O espaçador da esquerda usa a MESMA classe,
// que é o que equilibra a linha e deixa a logo no centro.
.lhs-icon {
  @apply flex items-center justify-center shrink-0;

  width: 30px;
  height: 30px;
}

.lhs-close svg {
  @apply w-full h-full;
}

.lhs-logo {
  @apply h-[24px] w-auto select-none;
}

// Só a lista rola (o painel clipa). `overscroll-behavior: contain` corta o
// encadeamento pro fundo quando o conteúdo chega no fim.
.lhs-scroll {
  @apply relative z-[1] text-left;

  flex: 1 1 auto;
  margin-top: 14px;
  padding-inline: var(--lhs-content-px, 16px);
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 24px);
  -webkit-overflow-scrolling: touch;

  // O conteúdo injetado (HTML do CMS, tabelas de prêmio) traz largura própria;
  // sem isto ele empurra a barra horizontal dentro do painel.
  :deep(img),
  :deep(video),
  :deep(table) {
    max-width: 100%;
  }
}

.lhs-title {
  @apply text-white text-center font-bold;

  margin-bottom: 16px;
  font-size: 18px;
  line-height: 26px;
}

@media (min-width: 1024px) {
  .lhs-overlay {
    @apply justify-center;
  }

  // Desktop: painel flutuante com cantos, igual ao hub — a largura vem do
  // consumidor (o post é uma coluna de texto; a lista de prêmios é mais larga).
  .lhs-screen {
    max-width: var(--lhs-desktop-width, 860px);
    height: min(88dvh, 900px);
    padding-top: 18px;
    border-radius: 22px;
  }

  // O padding lateral do conteúdo é sempre 16px aqui: `mobileContentPaddingX` só
  // vale no mobile.
  .lhs-scroll {
    padding-inline: 16px;
  }
}
</style>
